import { useCallback, useRef, useState } from 'react'
import sentryService from '../services/SentryService'

// From https://usehooks.com/useLocalStorage/

function useLocalStorage<T>(key: string, initialValue: T): [T, (value: T | ((val: T) => T)) => void] {
  // State to store our value
  // Pass initial state function to useState so logic is only executed once
  const [storedValue, setStoredValue] = useState<T>(() => {
    try {
      // Get from local storage by key
      const item = window.localStorage.getItem(key)
      // Parse stored json or if none return initialValue
      return item ? JSON.parse(item) : initialValue
    } catch (error) {
      // If error also return initialValue
      console.error(error)
      sentryService.captureError(error as Error, {
        tags: {
          errorType: 'localStorage',
          operation: 'read',
        },
        extra: {
          key,
          initialValue: typeof initialValue,
        },
      })
      return initialValue
    }
  })

  // Ref to store the latest value, so we can access it in the setter without needing to add it to the dependency array
  const latestValueRef = useRef(storedValue)

  // Return a wrapped version of useState's setter function that ...
  // ... persists the new value to localStorage.
  const setValue = useCallback(
    (value: T | ((val: T) => T)) => {
      try {
        // Allow value to be a function so we have same API as useState
        const valueToStore = value instanceof Function ? value(latestValueRef.current) : value
        // Save state
        latestValueRef.current = valueToStore
        setStoredValue(valueToStore)
        // Save to local storage
        window.localStorage.setItem(key, JSON.stringify(valueToStore))
      } catch (error) {
        // A more advanced implementation would handle the error case
        console.error(error)
        sentryService.captureError(error as Error, {
          tags: {
            errorType: 'localStorage',
            operation: 'write',
          },
          extra: {
            key,
            attemptedValue: typeof value,
          },
        })
      }
    },
    [key],
  )

  return [storedValue, setValue]
}

export default useLocalStorage
